import { t } from 'i18next'
import { cloneDeep } from 'lodash'
import create from 'zustand'

import { updateUser } from '@/src/data/locationUserDataProvider'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { isEasyOfferDashboardEnabled } from '@/src/shared/feature-flag/feature-flag.service'
import BudgetAndCostSVG from '@assets/widgets/BudgetAndCost.svg?url'
import ConsumptionTrendsSVG from '@assets/widgets/ConsumptionTrends.svg?url'
import DailyLoadProfileSVG from '@assets/widgets/DailyLoadProfile.svg?url'
import RegulationCalendarSVG from '@assets/widgets/RegulationCalendar.svg?url'
import SpotMarketSVG from '@assets/widgets/SpotMarket.svg?url'
import NewsSVG from '@assets/widgets/news.svg?url'
import { Button } from '@chakra-ui/react'
import { XCircle } from 'react-feather'
import { v4 as uuidv4 } from 'uuid'
import { persist } from 'zustand/middleware'

export const LAYOUT_COLS = 120
export const LAYOUT_ROW_HEIGHT = 24
const USED_WIDGETS_SUFFIXES = new Set()

export const WIDGET_IDS = {
  BUDGET_AND_COST: 'budget-and-cost',
  MAIN_CONSUMPTION_CARD: 'main-consumption-card',
  CONSUMPTION_TRENDS: 'consumption-trends',
  REGULATIONS_CALENDAR: 'regulations-calendar',
  LOCATION_AND_DEVICE: 'location-and-device',
  SPOT_MARKET_PRICE: 'spot-market-price',
  GAS_SPOT_MARKET_PRICE: 'gas-spot-market-price',
  NEWS: 'news',
  PROCUREMENT_PRICE: 'procurement-price',
  ENPI: 'enpi',
  NEW_INSIGHTS: 'new-insights',
  SYSTEM_STATUS: 'system-status',
}

const getAllWidgets = () => {
  return [
    {
      id: WIDGET_IDS.NEWS,
      label: t('dashboard.widgets.news.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.BUDGET_AND_COST,
      label: t('dashboard.widgets.budget_and_cost.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.SPOT_MARKET_PRICE,
      label: t('dashboard.widgets.spot_market.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.GAS_SPOT_MARKET_PRICE,
      label: t('dashboard.widgets.gas_spot_market.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.MAIN_CONSUMPTION_CARD,
      label: t('dashboard.widgets.main_consumption_card.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.CONSUMPTION_TRENDS,
      label: t('dashboard.widgets.consumption_trends.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.REGULATIONS_CALENDAR,
      label: t('dashboard.widgets.regulations_calendar.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    // average of 4 devices would be displayed
    {
      id: WIDGET_IDS.LOCATION_AND_DEVICE,
      label: t('dashboard.widgets.location_and_device_card.title') as string,
      maxInstances: 6,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.ENPI,
      label: t('dashboard.widgets.enpi.title') as string,
      maxInstances: 6,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.NEW_INSIGHTS,
      label: t('dashboard.widgets.new_insights.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
    {
      id: WIDGET_IDS.SYSTEM_STATUS,
      label: t('dashboard.widgets.system_status.title') as string,
      maxInstances: 1,
      instanceCount: 0,
    },
  ]
}

export const WIDGETS_SVGS = {
  [WIDGET_IDS.BUDGET_AND_COST]: BudgetAndCostSVG,
  [WIDGET_IDS.SPOT_MARKET_PRICE]: SpotMarketSVG,
  [WIDGET_IDS.GAS_SPOT_MARKET_PRICE]: SpotMarketSVG,
  [WIDGET_IDS.MAIN_CONSUMPTION_CARD]: DailyLoadProfileSVG,
  [WIDGET_IDS.CONSUMPTION_TRENDS]: ConsumptionTrendsSVG,
  [WIDGET_IDS.REGULATIONS_CALENDAR]: RegulationCalendarSVG,
  [WIDGET_IDS.LOCATION_AND_DEVICE]: DailyLoadProfileSVG,
  [WIDGET_IDS.NEWS]: NewsSVG,
  [WIDGET_IDS.ENPI]: ConsumptionTrendsSVG,
  [WIDGET_IDS.NEW_INSIGHTS]: NewsSVG,
  [WIDGET_IDS.SYSTEM_STATUS]: DailyLoadProfileSVG,
}

/**
 * The width (`w`) and height (`h`) of a widget can be customized by the user when creating a layout.
 * However, these values are constrained by `min/max` limits to ensure they stay within bounds.
 *
 * ⚠️ IMPORTANT:
 * - If the user has already created a custom layout, changes to `WIDGET_DIMENSIONS` will only take effect
 *   after logging out and back in.
 * - If changes in width don't seem to apply, check `useAdjustedDashboardLayout`, as it enforces a `minW`.
 *
 * 📌 Why does this happen?
 * Our grid layout adjusts its width based on screen size, while the height remains fixed.
 * To ensure widgets remain usable:
 * - Smaller screens require a minimum width in pixels to fit content.
 * - On larger screens, a `minW` set in grid units might become too restrictive,
 *   preventing users from resizing the widget below a certain threshold.
 *
 */
const WIDGET_DIMENSIONS = {
  [WIDGET_IDS.BUDGET_AND_COST]: { w: 70, h: 6, minH: 6, minW: 0, maxH: 6, maxW: LAYOUT_COLS },
  [WIDGET_IDS.SPOT_MARKET_PRICE]: { w: 50, h: 6, minH: 5, minW: 0, maxH: 50, maxW: LAYOUT_COLS },
  [WIDGET_IDS.GAS_SPOT_MARKET_PRICE]: { w: 50, h: 6, minH: 5, minW: 0, maxH: 50, maxW: LAYOUT_COLS },
  [WIDGET_IDS.MAIN_CONSUMPTION_CARD]: {
    w: LAYOUT_COLS,
    h: 8,
    minH: 8,
    minW: 0,
    maxH: 13,
    maxW: LAYOUT_COLS,
  },
  [WIDGET_IDS.LOCATION_AND_DEVICE]: {
    w: 120,
    h: 13,
    minH: 5,
    minW: 0,
    maxH: 13,
    maxW: LAYOUT_COLS,
  },
  [WIDGET_IDS.CONSUMPTION_TRENDS]: { w: 72, h: 5, minH: 5, minW: 0, maxH: 5, maxW: LAYOUT_COLS },
  [WIDGET_IDS.REGULATIONS_CALENDAR]: {
    w: 48,
    h: 5,
    minH: 5,
    minW: 0,
    maxH: 5,
    maxW: LAYOUT_COLS,
  },
  [WIDGET_IDS.NEWS]: {
    w: 48,
    h: 12,
    minH: 5,
    minW: 0,
    maxH: 12,
    maxW: LAYOUT_COLS,
  },
  [WIDGET_IDS.ENPI]: {
    w: LAYOUT_COLS,
    h: 11,
    minH: 5,
    minW: 0,
    maxH: 24,
    maxW: LAYOUT_COLS,
  },
  [WIDGET_IDS.NEW_INSIGHTS]: {
    w: 48,
    h: 18,
    minH: 8,
    minW: 0,
    maxH: 30,
    maxW: LAYOUT_COLS,
  },
  [WIDGET_IDS.SYSTEM_STATUS]: { w: 48, h: 5, minH: 5, minW: 0, maxH: 5, maxW: LAYOUT_COLS },
}

const DEFAULT_LAYOUT = [
  {
    i: WIDGET_IDS.BUDGET_AND_COST,
    x: 0,
    y: 0,
    ...WIDGET_DIMENSIONS[WIDGET_IDS.BUDGET_AND_COST],
  },
  {
    i: WIDGET_IDS.SPOT_MARKET_PRICE,
    x: 75,
    y: 0,
    ...WIDGET_DIMENSIONS[WIDGET_IDS.SPOT_MARKET_PRICE],
  },
  {
    i: WIDGET_IDS.MAIN_CONSUMPTION_CARD,
    x: 0,
    y: 13,
    ...WIDGET_DIMENSIONS[WIDGET_IDS.MAIN_CONSUMPTION_CARD],
  },
  {
    i: WIDGET_IDS.CONSUMPTION_TRENDS,
    x: 0,
    y: 18,
    ...WIDGET_DIMENSIONS[WIDGET_IDS.CONSUMPTION_TRENDS],
  },
  {
    i: WIDGET_IDS.REGULATIONS_CALENDAR,
    x: 72,
    y: 18,
    ...WIDGET_DIMENSIONS[WIDGET_IDS.REGULATIONS_CALENDAR],
  },
]

const EASY_OFFER_HALF_WIDTH = LAYOUT_COLS / 2
const EASY_OFFER_TOP_ROW_HEIGHT = 5
const EASY_OFFER_BOTTOM_ROW_HEIGHT = 12

const buildEasyOfferWidget = (widgetId: string, x: number, y: number, h: number) => ({
  ...WIDGET_DIMENSIONS[widgetId],
  i: widgetId,
  x,
  y,
  w: EASY_OFFER_HALF_WIDTH,
  h,
})

export const buildEasyOfferDashboardLayout = () => [
  buildEasyOfferWidget(WIDGET_IDS.CONSUMPTION_TRENDS, 0, 0, EASY_OFFER_TOP_ROW_HEIGHT),
  buildEasyOfferWidget(WIDGET_IDS.SYSTEM_STATUS, EASY_OFFER_HALF_WIDTH, 0, EASY_OFFER_TOP_ROW_HEIGHT),
  buildEasyOfferWidget(WIDGET_IDS.ENPI, 0, EASY_OFFER_TOP_ROW_HEIGHT, EASY_OFFER_BOTTOM_ROW_HEIGHT),
  buildEasyOfferWidget(WIDGET_IDS.NEW_INSIGHTS, EASY_OFFER_HALF_WIDTH, EASY_OFFER_TOP_ROW_HEIGHT, EASY_OFFER_BOTTOM_ROW_HEIGHT),
]

const getDefaultLayout = () => (isEasyOfferDashboardEnabled() ? buildEasyOfferDashboardLayout() : DEFAULT_LAYOUT)

const getWidgets = (layout: any) => {
  const allWidgets = getAllWidgets()
  for (const widget of layout) {
    const widgetId = widget.i.split('_')[0]
    const i = allWidgets.findIndex(widget => widget.id.startsWith(widgetId))
    allWidgets[i].instanceCount += 1
  }
  return allWidgets
}

// TODO: V Improve typing. Add interface.
const useDashboardEditingStore = create<any, any, any, any>(
  persist(
    (set, get) => {
      return {
        isInEditMode: false,
        layout: DEFAULT_LAYOUT,
        // layout while in edit mode
        tmpLayout: [],
        widgets: getAllWidgets(),
        // widgets while in edit mode
        tmpWidgets: [],
        initialize: userData => {
          get().fetchLayout(userData)
        },
        fetchLayout: userData => {
          try {
            if (userData.customDashboardLayout) {
              get().setLayout(userData.customDashboardLayout)
            } else {
              get().setLayout(getDefaultLayout())
            }
          } catch (err) {
            get().setLayout(getDefaultLayout())
          }
        },
        restoreDefaultLayout: async () => {
          await get().updateLayout(getDefaultLayout())
        },
        setLayout: layout => {
          layout.forEach(widget => {
            const i = widget.i.split('_')[0]
            const dimensions = WIDGET_DIMENSIONS[i]

            // Update to nearest bound if outside bounds
            if (widget.h < dimensions.minH) widget.h = dimensions.minH
            else if (widget.h > dimensions.maxH) widget.h = dimensions.maxH

            if (widget.w < dimensions.minW) widget.w = dimensions.minW
            else if (widget.w > dimensions.maxW) widget.w = dimensions.maxW

            widget.maxW = dimensions.maxW
            widget.minW = dimensions.minW
            widget.maxH = dimensions.maxH
            widget.minH = dimensions.minH
          })
          set({ layout })
          set({ widgets: getWidgets(layout) })
        },
        setTmpLayout: tmpLayout => {
          set({ tmpLayout })
          set({ tmpWidgets: getWidgets(tmpLayout) })
        },
        addToTmpLayout: (widgetId: string, payload: Record<string, unknown> = {}) =>
          set(state => {
            const currentLayout = [...state.tmpLayout]
            const PLACEHOLDER_WIDGET = { x: 0, y: 0, w: 0, h: 0 }
            let lastElementInGrid = currentLayout[0] ?? PLACEHOLDER_WIDGET

            // Find the last element in the grid
            for (let i = 0; i < currentLayout.length; i++) {
              const currentElement = currentLayout[i]
              if (currentElement.x >= lastElementInGrid.x) lastElementInGrid = currentElement
              if (currentElement.y >= lastElementInGrid.y) lastElementInGrid = currentElement
            }

            const newElementX = lastElementInGrid.x + lastElementInGrid.w

            // Find the tallest element in the same row
            const tallestElementInSameRow =
              currentLayout.filter(item => item.y === lastElementInGrid.y).sort((a, b) => b.h - a.h)[0] ?? PLACEHOLDER_WIDGET

            // Check if the widget will break to a new row
            const rowBreak = newElementX + WIDGET_DIMENSIONS[widgetId].w > LAYOUT_COLS

            let idSuffix = uuidv4()
            while (USED_WIDGETS_SUFFIXES.has(idSuffix)) idSuffix = uuidv4()
            USED_WIDGETS_SUFFIXES.add(idSuffix)

            // Create the new layout element
            const newLayoutElement = {
              i: `${widgetId}_${idSuffix}`,
              x: rowBreak ? 0 : newElementX,
              y: rowBreak ? lastElementInGrid.y + tallestElementInSameRow.h : lastElementInGrid.y,
              ...WIDGET_DIMENSIONS[widgetId],
              ...payload,
            }

            // Add the new element to the layout and update the state
            currentLayout.push(newLayoutElement)
            state.setTmpLayout(currentLayout)
          }),
        removeFromTmpLayout: widgetFullId =>
          set(state => {
            const currentLayout = [...get().tmpLayout]
            const newLayout = currentLayout.filter(item => item.i !== widgetFullId)
            get().setTmpLayout(newLayout)
          }),
        toggleIsInEditMode: async () => {
          const isInEditMode = get().isInEditMode
          if (!isInEditMode) {
            set({ isInEditMode: !isInEditMode })
            const editableLayout = cloneDeep(get().layout)
            get().setTmpLayout(editableLayout)
          } else {
            await get().updateLayout(get().tmpLayout)
            set({ isInEditMode: !isInEditMode, tmpWidgets: [], tmpLayout: [] })
          }
        },
        getAvailableWidgets: () => {
          // const unusedWidgets = get().tmpWidgets.filter(widget => widget.instanceCount < widget.maxInstances)
          // return unusedWidgets

          return get().tmpWidgets
        },
        updateLayout: async (updatedLayout, updateLayoutinDb = true) => {
          const userId = useSessionStore.getState().user?.id
          const companyId = useSessionStore.getState().user?.company.id

          if (userId) {
            const layoutWithoutUndefinedValues = updatedLayout.map(item => {
              const cleanedItem = { ...item }
              Object.keys(cleanedItem).forEach(key => {
                if (cleanedItem[key] === undefined) cleanedItem[key] = null // Replace undefined with null
              })
              return cleanedItem
            })

            await updateUser(userId, companyId, { customDashboardLayout: layoutWithoutUndefinedValues })
            useSessionStore.getState().updateUserInStore({ customDashboardLayout: updatedLayout })

            // Set layout locally
            get().setLayout(updatedLayout)
          }
        },
        useRemoveWidgetButton: widgetFullId => {
          return (
            <Button
              size='sm'
              position='absolute'
              zIndex={99}
              top={1}
              right={1}
              padding={0}
              h={'fit-content'}
              borderWidth={0}
              _focus={{ borderWidth: 0 }}
              _hover={{ bg: 'transparent' }}
              onMouseDown={e => {
                e.stopPropagation()
                e.preventDefault()
                get().removeFromTmpLayout(widgetFullId)
              }}
            >
              <XCircle />
            </Button>
          )
        },
      }
    },
    {
      name: 'dashboard-layout',
      partialize: (state: any) => ({ layout: state.layout, widgets: state.widgets }), // persists layout so no firestore call is needed on page refresh
    }
  )
)

export { useDashboardEditingStore }
